/**
 * Site footer.
 *
 * Every link column and every string comes from the CMS (`Menu` records plus `SiteSettings`), so
 * the footer can be restructured without a deployment. The newsletter form is real: it posts to
 * the leads module and reports success or failure honestly.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Input } from '@/components/ui';
import { Logo } from '@/components/site/Logo';
import { useMenu, useSiteSettings } from '@/context/SiteContext';
import { ApiError } from '@/services/client';
import { leadApi } from '@/services/api';
import type { MenuItem } from '@/services/types';

function LinkColumn({ title, items }: { title: string; items: MenuItem[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h2 className="text-eyebrow uppercase text-white/50">{title}</h2>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item.id}>
            <Link
              to={item.href || item.url}
              className="text-sm text-white/75 no-underline transition-colors hover:text-white hover:underline"
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialLinks() {
  const settings = useSiteSettings();
  const links = [
    { url: settings.linkedin_url, label: 'LinkedIn' },
    { url: settings.twitter_url, label: 'X' },
    { url: settings.youtube_url, label: 'YouTube' },
    { url: settings.github_url, label: 'GitHub' },
  ].filter((link) => Boolean(link.url));

  if (links.length === 0) return null;

  return (
    <ul className="flex flex-wrap gap-2">
      {links.map((link) => (
        <li key={link.label}>
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/80 no-underline transition-colors hover:border-white/40 hover:text-white hover:no-underline"
          >
            {link.label}
          </a>
        </li>
      ))}
    </ul>
  );
}

function NewsletterForm() {
  const settings = useSiteSettings();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setState('submitting');
    try {
      const result = await leadApi.subscribeNewsletter({ email, source_page: window.location.pathname });
      setState('done');
      setMessage(result.message);
      setEmail('');
    } catch (error) {
      setState('error');
      setMessage(
        error instanceof ApiError
          ? (error.fieldError('email') ?? error.message)
          : 'Something went wrong. Please try again.',
      );
    }
  }

  if (!settings.enable_newsletter) return null;

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-6">
      <h2 className="font-display text-base font-semibold text-white">
        {settings.newsletter_heading || 'Technology briefings, quarterly'}
      </h2>
      {settings.newsletter_body && <p className="mt-2 text-sm text-white/65">{settings.newsletter_body}</p>}

      {state === 'done' ? (
        <Alert tone="success" className="mt-4 border-white/20 bg-white/10 text-white">
          {message}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <label htmlFor="newsletter-email" className="sr-only">
            Email address
          </label>
          <Input
            id="newsletter-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@organisation.com"
            error={state === 'error'}
            className="border-white/20 bg-white/10 text-white placeholder:text-white/40"
          />
          <Button type="submit" variant="inverse" loading={state === 'submitting'} className="shrink-0">
            Subscribe
          </Button>
        </form>
      )}

      {state === 'error' && (
        <p role="alert" className="mt-2 text-xs font-medium text-signal">
          {message}
        </p>
      )}
    </div>
  );
}

export function Footer() {
  const settings = useSiteSettings();
  const solutions = useMenu('footer-solutions');
  const company = useMenu('footer-company');
  const resources = useMenu('footer-resources');
  const legal = useMenu('footer-legal');

  return (
    <footer className="band-dark border-t border-white/10">
      <div className="shell py-16">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_2fr]">
          <div className="space-y-6">
            <Logo inverse />
            {settings.footer_about && <p className="max-w-sm text-sm leading-relaxed text-white/65">{settings.footer_about}</p>}

            <address className="space-y-2 text-sm not-italic text-white/65">
              {settings.address_lines_list.map((line) => (
                <p key={line}>{line}</p>
              ))}
              {(settings.city || settings.postal_code) && (
                <p>
                  {[settings.city, settings.state, settings.postal_code].filter(Boolean).join(', ')}
                  {settings.country && `, ${settings.country}`}
                </p>
              )}
              {settings.primary_phone && (
                <p>
                  <a href={`tel:${settings.primary_phone.replace(/\s/g, '')}`} className="text-white/80 no-underline hover:underline">
                    {settings.primary_phone}
                  </a>
                </p>
              )}
              {settings.primary_email && (
                <p>
                  <a href={`mailto:${settings.primary_email}`} className="text-white/80 no-underline hover:underline">
                    {settings.primary_email}
                  </a>
                </p>
              )}
            </address>

            <SocialLinks />
          </div>

          <div className="space-y-10">
            <div className="grid gap-8 sm:grid-cols-3">
              <LinkColumn title="Solutions" items={solutions} />
              <LinkColumn title="Company" items={company} />
              <LinkColumn title="Resources" items={resources} />
            </div>
            <NewsletterForm />
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} {settings.copyright_text || `${settings.company_name}. All rights reserved.`}
            {settings.registration_number && <span className="ml-2">CIN: {settings.registration_number}</span>}
          </p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {legal.map((item) => (
              <li key={item.id}>
                <Link to={item.href || item.url} className="text-xs text-white/50 no-underline hover:text-white/80 hover:underline">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
